import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import { Shield, Mail, Lock, Eye, EyeOff, Loader2 } from 'lucide-react';
import './Login.css';

const Login = () => {
  const { t } = useTranslation();
  const { login } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.email || !formData.password) {
      const errorMsg = t('errors.requiredFields');
      setError(errorMsg);
      toast.error(errorMsg);
      return;
    }
    
    setLoading(true);
    setError('');
    
    const result = await login(formData.email, formData.password, rememberMe);
    
    if (result.success) {
      toast.success(t('auth.loginSuccess') || 'تم تسجيل الدخول بنجاح');
      navigate('/dashboard');
    } else {
      const errorMsg = result.error || t('auth.invalidCredentials');
      setError(errorMsg);
      toast.error(errorMsg);
      // مسح البيانات من الفورم بعد الخطأ
      setFormData({ email: '', password: '' });
    }
    
    setLoading(false);
  };

  return (
    <div className="login-page">
      <div className="login-container">
        <div className="login-card">
          <div className="login-header">
            <div className="login-icon">
              <Shield size={40} />
            </div>
            <h1>{t('auth.adminLogin')}</h1>
            <p>{t('auth.adminLoginSubtitle')}</p>
          </div>

          <form className="login-form" onSubmit={handleSubmit}>
            {error && (
              <div className="error-message">
                {error}
              </div>
            )}

            <div className="form-group">
              <label className="form-label">{t('auth.email')}</label>
              <div className="input-wrapper">
                <Mail size={20} />
                <input
                  type="email"
                  name="email"
                  className="form-input"
                  placeholder={t('auth.emailPlaceholder')}
                  value={formData.email}
                  onChange={handleChange}
                  disabled={loading}
                />
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">{t('auth.password')}</label>
              <div className="input-wrapper">
                <Lock size={20} />
                <input
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  className="form-input"
                  placeholder={t('auth.passwordPlaceholder')}
                  value={formData.password}
                  onChange={handleChange}
                  disabled={loading}
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>

            <div className="remember-me-row">
              <label className="remember-me-label">
                <input
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  disabled={loading}
                  className="remember-me-checkbox"
                />
                <span>تذكرني</span>
              </label>
            </div>

            <button
              type="submit"
              className="btn btn-primary btn-lg login-btn"
              disabled={loading}
            >
              {loading ? (
                <>
                  <Loader2 size={20} className="spinner" />
                  {t('auth.loggingIn')}
                </>
              ) : (
                t('auth.loginButton')
              )}
            </button>
          </form>
        </div>

        <div className="login-info">
          <div className="info-content">
            <h2>{t('common.adminPanel')}</h2>
            <p>{t('auth.adminPanelDescription')}</p>
            <ul className="features-list">
              <li>{t('auth.feature1')}</li>
              <li>{t('auth.feature2')}</li>
              <li>{t('auth.feature3')}</li>
              <li>{t('auth.feature4')}</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
